import type { BioSiteStyleBackground } from "./background";
import type { BioSiteStyleFontType } from "./fonts";
import type { BioSiteStyleLayoutType } from "./layout";
import type { Media } from "./media";
import type {
  Location,
  OrieEvent,
  OrieGiftcard,
  OrieProduct,
  OrieService,
} from "./resource";
import type { BioSiteSection, BioSiteStyleSection } from "./section";
import type { BioSiteSocialItem, BioSiteSocialsType } from "./socials";

export enum BioSiteStatus {
  DRAFT = "DRAFT",
  ACTIVE = "ACTIVE",
  UNPUBLISHED_CHANGES = "UNPUBLISHED_CHANGES",
  ARCHIVED = "ARCHIVED",
}

export enum BioSiteScriptType {
  // GTM = "gtm",
  // GA = "ga",
  // Pixel = "pixel",
  CUSTOM = "custom",
}

export enum BioSiteStylePageControlsAlignment {
  LEFT = "left",
  CENTER = "center",
  JUSTIFY = "justify",
  RIGHT = "right",
}

export enum BioSiteStylePageControlsVariant {
  TEXT = "text",
  BUTTON = "button",
}

export interface BioSiteStyleText {
  font?: BioSiteStyleFontType;
  color?: string;
}

export interface BioSiteStylePageControls {
  hideSlider?: boolean;
  stacked?: boolean;
  variant?: BioSiteStylePageControlsVariant;
  align?: BioSiteStylePageControlsAlignment;
}

export interface BioSiteStyle {
  layout: BioSiteStyleLayoutType;
  theme?: string;
  background?: BioSiteStyleBackground;
  section?: BioSiteStyleSection;
  text?: BioSiteStyleText;
  pageControls: BioSiteStylePageControls;
}

export interface BioSiteSeoMeta {
  id?: string;
  content: string;
  name?: string;
  property?: string;
}

export interface BioSiteSeo {
  title: string;
  description?: string;
  // keywords?: string[];
  meta: BioSiteSeoMeta[];
}

export interface BioSiteScript {
  id?: string;
  type: BioSiteScriptType;
  placement: "head"; // | "body";
  async?: boolean;
  defer?: boolean;
  src?: string;
  content?: string;
}

export interface BioSiteSettings {
  sensitiveContent: boolean;
  hideOrieLogo: boolean;
  subscriptions: boolean;
  integrations?: {
    google?: {
      measurementId: string;
    };
    facebook?: {
      pixelId: string;
      conversionsApiAccessToken: string;
    };
  };
  pages: {
    default?: string;
  };
}

export interface BioSitePage {
  id: string;
  title: string;
  slug: string;
  description?: string;
  icon?: string;
  position: number;
  hidden: boolean; // nav isn't visible but page is still accessible by link
  disabled: boolean;
  sections: BioSiteSection[];
}

export interface BioSiteData {
  name: string;
  bio?: string;
  image: string;
  socials?: BioSiteSocials;
  age_restriction?: string;
  style: BioSiteStyle;
  seo?: BioSiteSeo;
  pages: BioSitePage[];
  scripts: BioSiteScript[];
  settings: BioSiteSettings;
}

export interface BioSiteSocials {
  invalid: boolean;
  disabled: boolean;
  socials: Partial<Record<BioSiteSocialsType, BioSiteSocialItem>>;
  bottom: boolean;
}

export interface BioSiteResources {
  latestEvent?: string;
  files?: Record<string, Media>;
  products?: Record<string, OrieProduct>;
  productVariants?: Record<string, OrieProduct>;
  services?: Record<string, OrieService>;
  serviceVariants?: Record<string, OrieService>;
  events?: Record<string, OrieEvent>;
  eventTickets?: Record<string, OrieEvent>;
  locations?: Record<string, Location>;
  giftcards?: Record<string, OrieGiftcard>;
  profiles?: Partial<{
    shop: string;
    event: string;
    service: string;
  }>;
}

export interface BioSiteApi {
  pk: string;
  channel: string;
  scopes: string[];
}

export interface BioSite {
  id: string;
  object: string;
  handle: string;
  name: string;
  workspace?: string;
  status: BioSiteStatus;
  verified: boolean;
  api: BioSiteApi;
  data: BioSiteData;
  resources?: BioSiteResources;
}

export interface BioSiteStateTransfer {
  controls: {
    page: string;
  };
  data: Partial<BioSite>;
}
